import { useDebounceFn } from '@vueuse/core';
import isMatchWith from 'lodash/isMatchWith';
import { type MaybeRefOrGetter, toValue, watch } from 'vue';

export function useChangeListener<T extends object>(
    initialValue: MaybeRefOrGetter<T>,
    value: MaybeRefOrGetter<T>,
    callback: (equals: boolean, value: T) => void,
) {
    const changeListener = useDebounceFn(() => {
        const _initialValue = toValue(initialValue);
        const _value = toValue(value);

        // partial comparsion, because value may miss readonly props or ids
        const equals = isMatchWith(_initialValue, _value, (a, b) => {
            if((a && typeof a === 'object') || (b && typeof b === 'object')) return undefined;
            // treat nulls, undefineds and "" as the same
            if(a === null || a === undefined || a === '') {
                if(b === null || b === undefined || b === '') return true;
                return false;
            }
            const same = String(a) === String(b);
            return same;
        });

        // console.log(`changed => ${!equals}`);
        callback(equals, _value);
    }, 200);

    watch(initialValue, () => {
        changeListener();
    });

    watch(value, () => {
        changeListener();
    }, { deep: true });
}
